// @tnb/output-cases
// Canonical per-destination "Cases by output location" split — derived on read so it ALWAYS
// tracks the session's authoritative box total (`total_boxes`), never a stored go-stale value.
//
// Background (WCO-2477 / WCO-2546): the manager's planned per-destination split is persisted on
// `ics.hte_production_output_session.expected_cases` as a RATIO/shape. When a session receives more
// raw material, the receive-and-verify recompute updates `total_boxes` but NOT `expected_cases`, so
// the stored split goes stale. Session Details already rescaled it on read (WCO-2477); WCO-2546
// moves that same rescale into every reader (transfer-out targets, output scan summary) so all
// supervisor surfaces agree. `total_boxes` is the single source of truth (WCO-2469); the split is
// advisory guidance (the physical scan is the authoritative shipping allocation).
//
// The rescale is proportional with the LARGEST-REMAINDER method so the parts sum EXACTLY to the
// total (no rounding drift), preserving the manager's authored ratio.

// Rescale integer weights so they sum EXACTLY to `total`, proportional to the input weights.
// `total` MUST be a non-negative integer (the largest-remainder algorithm assumes it); every caller
// passes a `::int`-cast box count. No-op (returns a copy of the input) when `total` is not a positive
// integer (0, negative, NaN, Infinity, or a fraction) or the weights carry no signal (all 0) — this
// keeps the "always sums exactly to total" contract from silently breaking on a bad total.
// weights: number[]  ->  number[] of the same length.
function rescaleSplit(weights, total) {
  const w = (weights || []).map((x) => Number(x) || 0);
  const sumW = w.reduce((a, b) => a + b, 0);
  if (!w.length || !Number.isInteger(total) || total <= 0 || sumW <= 0) return w.slice();
  const raw = w.map((x) => (total * x) / sumW);
  const parts = raw.map((r) => Math.floor(r));
  let remainder = total - parts.reduce((a, b) => a + b, 0);
  const byFrac = raw
    .map((r, i) => ({ i, frac: r - Math.floor(r) }))
    .sort((a, b) => b.frac - a.frac);
  for (let k = 0; remainder > 0 && byFrac.length > 0; k++, remainder--) {
    parts[byFrac[k % byFrac.length].i]++;
  }
  return parts;
}

// Frontend-shaped wrapper: re-split `destinations` so their expected/plan cases sum to `total`.
// Returns the destination objects with `expected_cases` and `cases` set to the rescaled parts
// (`actual_cases` and every other field untouched). No-op (returns the input array unchanged)
// when `total <= 0` or the split carries no signal (all null/0). Behaviourally identical to the
// former services/admin-frontend/src/utils/outputCases.ts deriveOutputCases().
function deriveOutputCases(destinations, total) {
  if (!Array.isArray(destinations) || destinations.length === 0) return destinations;
  const weights = destinations.map((d) => Number(d.expected_cases != null ? d.expected_cases : d.cases) || 0);
  const sumW = weights.reduce((a, b) => a + b, 0);
  // Same precondition as rescaleSplit: only a positive integer total triggers a re-split; anything else
  // (0, negative, NaN, Infinity, fraction) returns the input UNCHANGED — a true no-op (not a re-mapped copy).
  if (!Number.isInteger(total) || total <= 0 || sumW <= 0) return destinations;
  const parts = rescaleSplit(weights, total);
  return destinations.map((d, i) => Object.assign({}, d, { expected_cases: parts[i], cases: parts[i] }));
}

module.exports = { rescaleSplit, deriveOutputCases };
